import { useEffect, useRef, useState, useCallback } from 'react';

const reasons = [
  { n: '01', title: 'Renta vacacional garantizada',   desc: 'Ubicación natural única y diseño premium aseguran alta demanda en plataformas como Airbnb durante todo el año.' },
  { n: '02', title: 'Comodidades de resort exclusivo',  desc: 'Área social, parrillas, gimnasio, spa, lounge, comidas y deportes — todo dentro del condominio privado.' },
  { n: '03', title: 'Arquitectura minimalista premium',desc: 'Diseño moderno con acabados de primera, integrado armoniosamente con el entorno natural cajamarquino.' },
  { n: '04', title: 'Alta plusvalía sostenida',        desc: 'Zona de crecimiento turístico acelerado. Baños del Inca es uno de los destinos con mayor proyección en el norte del Perú.' },
  { n: '05', title: 'Administración profesional',      desc: 'Gestión integral para facilitar la operación en renta sin que el propietario deba preocuparse por nada.' },
];

const CARD_VH   = 5;
const OUTRO_VH  = 6.5;
const TOTAL_VH  = CARD_VH + OUTRO_VH;

function ease(t) { return t < .5 ? 2*t*t : -1+(4-2*t)*t; }
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function progress(v, from, to) { return clamp((v - from) / (to - from), 0, 1); }

/* ── Ripple button ────────────────────────────────────── */
function RippleButton() {
  const [ripple,  setRipple]  = useState(null);
  const [hovered, setHovered] = useState(false);
  const btnRef    = useRef(null);
  const rippleRef = useRef(null);
  const leaveTimer = useRef(null);

  const onEnter = useCallback((e) => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
    const r = btnRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const size = Math.sqrt(Math.max(x, r.width - x) ** 2 + Math.max(y, r.height - y) ** 2) * 2 + 4;
    setRipple({ x, y, size, id: Date.now() });
  }, []);

  const onLeave = useCallback(() => {
    setHovered(false);
    // Reverse the ripple on the live DOM node — no re-mount, no jump
    if (rippleRef.current) {
      rippleRef.current.style.animation = 'wi-ripple-out 0.75s cubic-bezier(.4,0,.2,1) forwards';
    }
    leaveTimer.current = setTimeout(() => setRipple(null), 750);
  }, []);

  return (
    <button
      ref={btnRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
      style={{
        position: 'relative', overflow: 'hidden',
        background: 'transparent',
        border: '1.5px solid #000',
        padding: '16px 40px', borderRadius: 100,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', letterSpacing: '.03em',
        userSelect: 'none',
      }}
    >
      {/* Black fill ripple */}
      {ripple && (
        <span
          key={ripple.id}
          ref={rippleRef}
          style={{
            position: 'absolute', borderRadius: '50%',
            background: '#000', pointerEvents: 'none',
            left: ripple.x, top: ripple.y,
            width: ripple.size, height: ripple.size,
            transform: 'translate(-50%,-50%) scale(0)',
            animation: 'wi-ripple 0.9s cubic-bezier(.4,0,.2,1) forwards',
          }}
        />
      )}
      {/* Text — transitions black → white when ripple covers it, back on leave */}
      <span style={{
        position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 10,
        color: hovered ? '#fff' : '#000',
        transition: hovered ? 'color 0.18s ease 0.28s' : 'color 0.3s ease',
      }}>
        Quiero invertir en este proyecto
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7v10"/>
        </svg>
      </span>
    </button>
  );
}

/* ── Main section ─────────────────────────────────────── */
export default function WhyInvest() {
  const [active,       setActive]       = useState(0);
  const [outroP,       setOutroP]       = useState(0);
  const [vw,           setVw]           = useState(() => document.documentElement.clientWidth);
  const [vh,           setVh]           = useState(() => window.innerHeight);
  const [BW,           setBW]           = useState(240);
  const [BH,           setBH]           = useState(54);
  const wrapperRef    = useRef(null);
  const btnContainerRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      const btn = btnContainerRef.current?.querySelector('button');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      if (r.width > 0) { setBW(r.width); setBH(r.height); }
    };
    // measure after fonts are ready so text width is accurate
    document.fonts.ready.then(measure);
    const resize = () => {
      setVw(document.documentElement.clientWidth);
      setVh(window.innerHeight);
      measure();
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const scrolled = -el.getBoundingClientRect().top;
      const vh = window.innerHeight;

      // Cards: exactly CARD_VH * 100vh, independent of OUTRO_VH
      const cardPx = CARD_VH * vh;
      const cardP  = clamp(scrolled / cardPx, 0, 1);
      setActive(Math.min(reasons.length - 1, Math.floor(cardP * reasons.length)));

      // Outro: starts after card phase, exactly OUTRO_VH * 100vh
      const outroP = clamp((scrolled - cardPx) / (OUTRO_VH * vh), 0, 1);
      setOutroP(outroP);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Clip-path: image shrinks to button shape centered on screen
  const imgP    = ease(progress(outroP, 0.05, 0.72));
  const insetX  = imgP * (vw - BW) / 2;
  const insetY  = imgP * (vh - BH) / 2;
  const radiusP = ease(progress(outroP, 0.05, 0.35));
  const radius  = radiusP * (BH / 2);

  const imgOpacity     = 1 - progress(outroP, 0.55, 0.78);
  const contentOpacity = 1 - progress(outroP, 0,    0.25);
  const blackOverlay   =     progress(outroP, 0.40,  0.75);
  const btnOpacity     =     progress(outroP, 0.65,  0.82);

  return (
    <>
      <style>{`
        @keyframes wi-ripple {
          from { transform: translate(-50%,-50%) scale(0); }
          to   { transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes wi-ripple-out {
          from { transform: translate(-50%,-50%) scale(1); }
          to   { transform: translate(-50%,-50%) scale(0); }
        }
      `}</style>

      <section id="invertir">
        <div ref={wrapperRef} style={{ height: `${TOTAL_VH * 100}vh` }}>
          <div className="sticky top-0 h-screen overflow-hidden" style={{ background: '#def0dd' }}>

            {/* ── Background image (shrinks via clip-path) ── */}
            <div style={{
              position: 'absolute', inset: 0,
              clipPath: `inset(${insetY}px ${insetX}px round ${radius}px)`,
              opacity: imgOpacity,
            }}>
              <img src="/assets/invertir/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/80" />
            </div>

            {/* ── Site-color overlay (reveals background during outro) ── */}
            <div style={{
              position: 'absolute', inset: 0, background: '#def0dd',
              opacity: blackOverlay, pointerEvents: 'none',
            }} />

            {/* ── Scrollable content (fades out at outro start) ── */}
            <div
              className="relative z-10 h-full flex flex-col justify-between px-8 md:px-16 pt-20 pb-14"
              style={{ opacity: contentOpacity }}
            >
              <div>
                <p className="text-xs tracking-[.28em] text-amber-400 uppercase font-medium mb-4">La decisión</p>
                <h2 className="text-3xl md:text-5xl font-light text-white leading-tight max-w-2xl">
                  ¿Por qué elegir{' '}
                  <em className="text-amber-400" style={{ fontStyle: 'italic' }}>"El Alto de los Bosques"</em>?
                </h2>
              </div>

              {/* ── Active card — large & prominent ── */}
              <div>
                <div className="relative min-h-[230px] md:min-h-[130px]">
                  {reasons.map((r, i) => (
                    <div key={r.n} style={{
                      position: 'absolute', inset: 0,
                      opacity: i === active ? 1 : 0,
                      transform: i === active ? 'translateY(0)' : i < active ? 'translateY(-14px)' : 'translateY(14px)',
                      transition: 'opacity .5s ease, transform .5s ease',
                      pointerEvents: i === active ? 'auto' : 'none',
                    }}>
                      <p style={{ fontSize: '.7rem', fontFamily: 'monospace', letterSpacing: '.25em', color: 'rgb(251,191,36)', marginBottom: '.6rem' }}>{r.n}</p>
                      <h3 style={{ color: '#fff', fontWeight: 700, fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', lineHeight: 1.2, marginBottom: '.75rem', maxWidth: 620 }}>{r.title}</h3>
                      <p style={{ color: 'rgba(255,255,255,.75)', fontSize: '1rem', lineHeight: 1.65, fontWeight: 300, maxWidth: 580 }}>{r.desc}</p>
                    </div>
                  ))}
                </div>

                {/* ── Indicators ── */}
                <div className="grid grid-cols-5 gap-2 md:gap-4 mt-6 md:mt-10">
                  {reasons.map((r, i) => {
                    const on = i === active;
                    return (
                      <div key={r.n} style={{
                        borderTop: `2px solid ${on ? 'rgb(251,191,36)' : 'rgba(255,255,255,.15)'}`,
                        paddingTop: '.6rem',
                        transition: 'border-color .45s ease',
                      }}>
                        <p style={{ fontSize: '.6rem', fontFamily: 'monospace', letterSpacing: '.2em', color: on ? 'rgb(251,191,36)' : 'rgba(255,255,255,.3)', marginBottom: '.3rem', transition: 'color .4s ease' }}>{r.n}</p>
                        <p className="hidden md:block" style={{ color: on ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.3)', fontSize: '.75rem', lineHeight: 1.35, transition: 'color .4s ease' }}>{r.title}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── CTA button (fades in centered at outro end) ── */}
            <div ref={btnContainerRef} style={{
              position: 'absolute', inset: 0, zIndex: 30,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: btnOpacity,
              pointerEvents: btnOpacity > 0.08 ? 'auto' : 'none',
            }}>
              <RippleButton />
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
